import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.tsx";
import { authFetch } from "../lib/api.ts";
import { ChevronRightIcon } from "../components/Icon.tsx";

interface Business {
  id: string;
  slug: string;
  name: string;
  role: string;
}

const DEV_BYPASS = !!import.meta.env.VITE_DEV_BYPASS_USER_ID;

export default function BusinessSelector() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (DEV_BYPASS) {
      navigate("/test-business", { replace: true });
      return;
    }
    authFetch("/businesses")
      .then((res) => res.json())
      .then((data: Business[]) => {
        setBusinesses(data);
        if (data.length === 1) {
          navigate(`/${data[0].slug}`, { replace: true });
        }
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: "var(--canvas)" }}
      >
        <div style={{ fontSize: 13, color: "var(--ink-faint)" }}>Loading…</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ background: "var(--canvas)" }}
    >
      <div className="w-full max-w-md">
        <div style={{ marginBottom: 32, textAlign: "center" }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10.5,
              fontWeight: 500,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--ink-faint)",
              marginBottom: 14,
            }}
          >
            Workspace
          </div>
          <h1
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 500,
              fontSize: 32,
              letterSpacing: "-0.018em",
              color: "var(--ink)",
            }}
          >
            Choose a business.
          </h1>
          <p
            style={{
              marginTop: 10,
              fontSize: 14,
              lineHeight: 1.55,
              color: "var(--ink-soft)",
              maxWidth: "36ch",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            You're a member of more than one — pick the one you want to work in. You can switch later from the masthead.
          </p>
        </div>

        {error && (
          <div className="alert-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        {!error && businesses.length === 0 ? (
          <div
            style={{
              padding: 28,
              background: "var(--paper-raised)",
              borderRadius: 14,
              boxShadow: "var(--shadow-sm)",
              textAlign: "center",
            }}
          >
            <h3
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 500,
                fontSize: 22,
                color: "var(--ink)",
                margin: "0 0 8px",
              }}
            >
              No businesses yet.
            </h3>
            <p style={{ fontSize: 14, color: "var(--ink-soft)", margin: "0 0 20px", lineHeight: 1.55 }}>
              Forty-five days free. No card required. Two minutes from now you'll have a booking page customers can actually use.
            </p>
            <button
              onClick={() => navigate("/create-business")}
              className="zd-hero-cta"
              style={{ width: "100%" }}
            >
              Create your first business →
            </button>
            <p style={{ fontSize: 12, color: "var(--ink-faint)", margin: "16px 0 0" }}>
              Already a member? Ask the owner of your business to invite you from their Team page.
            </p>
          </div>
        ) : (
          <>
          <ul style={{ display: "flex", flexDirection: "column", gap: 8, listStyle: "none", padding: 0, margin: 0 }}>
            {businesses.map((biz) => {
              const glyph = biz.name.trim()[0]?.toUpperCase() ?? "?";
              return (
                <li key={biz.id}>
                  <button
                    onClick={() => navigate(`/${biz.slug}`)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: "16px 18px",
                      background: "var(--paper-raised)",
                      borderRadius: 14,
                      boxShadow: "var(--shadow-sm)",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "transform 120ms ease, box-shadow 120ms ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "var(--shadow-md)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                    }}
                  >
                    <span
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        background: "var(--accent)",
                        color: "white",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {glyph}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontWeight: 600, fontSize: 14.5, color: "var(--ink)" }}>
                        {biz.name}
                      </span>
                      <span style={{ display: "block", fontSize: 12.5, color: "var(--ink-soft)", marginTop: 2 }}>
                        {biz.slug} <span style={{ color: "var(--ink-faint)" }}>·</span>{" "}
                        <span style={{ textTransform: "capitalize" }}>{biz.role}</span>
                      </span>
                    </span>
                    <span style={{ color: "var(--ink-faint)", flexShrink: 0 }}>
                      <ChevronRightIcon size={18} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            onClick={() => navigate("/create-business")}
            style={{
              marginTop: 16,
              width: "100%",
              padding: "12px 16px",
              background: "transparent",
              border: "1px dashed var(--rule-strong)",
              borderRadius: 14,
              fontSize: 13.5,
              color: "var(--ink-soft)",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            + Add a new business
          </button>
          <p style={{ fontSize: 11.5, color: "var(--ink-faint)", marginTop: 10, textAlign: "center", lineHeight: 1.5 }}>
            Heads up — the 45-day free trial covers your first business only. Adding more requires a paid plan.
          </p>
          </>
        )}

        <div style={{ marginTop: 28, textAlign: "center" }}>
          <button
            onClick={handleSignOut}
            style={{
              fontSize: 12.5,
              color: "var(--ink-faint)",
              background: "none",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
